// فهرستِ «کسانی که از بیرونِ اپ دعوت می‌شن» — ایمیل و شماره، توی یک فهرست.
//
// ═══ چرا جدا از emailInvites.ts ═══
//
// `emailInvites.ts` فرمِ **ساخت** را می‌سازد و فقط ایمیل می‌شناسد، چون آن‌جا
// شماره‌ها از دفترچه‌ی گوشی می‌آیند نه از تایپِ دستی. فرمِ **ویرایش** دفترچه
// ندارد (WHOSUP-203): آدم را همان‌جا با آدرسش اضافه می‌کنی، و آن آدرس می‌تواند
// ایمیل باشد یا شماره. پس یک فهرست با دو کانال.
//
// منطقِ خالص و بی‌ایمپورتِ نیتیو، چون هر دو اپ همین رفتار را می‌خواهند و دو
// بار نوشتنش یعنی دو رفتارِ کمی متفاوت. سرور همین قاعده‌ها را دوباره دارد
// (`invite_by_email` در مایگریشن 0066 و `invite_by_sms` در 0067) — این‌جا فقط
// برای اینه که کاربر **قبل از** فرستادن بفهمد، نه بعدش.
import { normalizeEmail, normalizePhone, phoneKey } from './contacts.ts';
import { MAX_EMAIL_INVITES } from './emailInvites.ts';
import { contactFriendsToShow } from './friendList.ts';

/** از چه راهی به این آدم می‌رسیم. همان دو مقدارِ ستونِ `signal_invites.channel`. */
export type InviteChannel = 'email' | 'sms';

export interface DraftContact {
  channel: InviteChannel;
  /** ایمیلِ نرمال‌شده، یا شماره‌ی بین‌المللی — همان چیزی که به سرور می‌رود. */
  value: string;
  name: string | null;
  /**
   * از کدام ردیفِ دفترچه آمده (WHOSUP-231). نیامده یعنی کاربر آدرس را همان
   * لحظه تایپ کرده.
   *
   * فقط برای نشان دادن است، نه برای سرور: با این، فرم می‌داند کدام چیپ مالِ
   * انتخابگرِ آدم‌هاست (و همان‌جا هم دیده می‌شود) و کدام مالِ ورودیِ دستی —
   * وگرنه مخاطبی که از فهرست انتخاب شده دو بار روی صفحه می‌نشست.
   */
  contactId?: number;
}

export type AddContactResult =
  | { ok: true; list: DraftContact[] }
  | { ok: false; reason: 'invalid' | 'duplicate' | 'full' };

export interface AddContactOptions {
  /** کدِ کشورِ پیش‌فرضِ شماره‌ی بدونِ «+» — از `phoneRepo.myCallingCode()`. */
  callingCode?: string;
  /**
   * چند نفرِ دیگر از هر کانال جا هست.
   *
   * سقفِ سرور برای **هر کانال جدا** ۵۰ تاست و روی ردیف‌هایی حساب می‌شود که
   * همین حالا توی دیتابیس‌اند. فرمِ ویرایش — برخلافِ فرمِ ساخت — روی سیگنالی
   * کار می‌کند که از قبل دعوت‌شده دارد، پس اگر این‌جا از صفر می‌شمردیم کاربر
   * می‌توانست پنجاه‌تای دیگر اضافه کند و سرور بی‌توضیح ردشان کند.
   */
  room?: Partial<Record<InviteChannel, number>>;
  /**
   * آدرس‌هایی که از قبل روی همین سیگنال دعوت شده‌اند (از {@link invitedSoFar}).
   *
   * سرور این‌ها را بی‌صدا رد می‌کند و توی `skipped` می‌شمارد. بدونِ این، چیپ
   * اضافه می‌شد، ذخیره سبز می‌شد، و هیچ دعوتی نمی‌رفت — بدترین شکلِ ممکن.
   */
  taken?: readonly string[];
  /** ردیفِ دفترچه‌ای که این آدرس از آن آمده — نگاه کن به {@link DraftContact.contactId}. */
  contactId?: number;
}

/**
 * ایمیل است یا شماره؟
 *
 * قاعده عمداً ساده و قابلِ پیش‌بینی‌ست: **«@» یعنی ایمیل، وگرنه شماره**.
 * آزمونِ پیچیده‌تر (مثلاً «رقم دارد پس شماره است») روی `test+1@x.com` یا
 * `ali2@x.com` اشتباه جواب می‌دهد، و کاربری که یک ایمیل تایپ کرده و پیامِ
 * «شماره‌ی نامعتبر» گرفته، نمی‌فهمد چه شد.
 */
export const channelOf = (raw: string): InviteChannel =>
  (raw.includes('@') ? 'email' : 'sms');

/** همان کلیدی که تکراری بودن با آن سنجیده می‌شود. شماره با ده رقمِ آخرش. */
const keyOf = (c: DraftContact): string =>
  (c.channel === 'email' ? `email:${c.value}` : `sms:${phoneKey(c.value) ?? c.value}`);

/**
 * یک نفر را به فهرست اضافه می‌کند.
 *
 * سه دلیلِ ردشدن از هم جدا برمی‌گردند چون پیامِ هرکدام فرق دارد: «آدرس نیست»،
 * «قبلاً اضافه‌ش کردی»، «به سقف خوردی». یک `false` برای هر سه یعنی کاربر
 * نمی‌فهمد کدامش بوده.
 */
export function addDraftContact(
  list: readonly DraftContact[],
  raw: string,
  rawName?: string,
  opts: AddContactOptions = {},
): AddContactResult {
  const channel = channelOf((raw ?? '').trim());
  const value = channel === 'email'
    ? normalizeEmail(raw)
    : normalizePhone(raw, opts.callingCode ?? '1');
  if (!value) return { ok: false, reason: 'invalid' };

  const name = (rawName ?? '').trim();
  const fresh: DraftContact = { channel, value, name: name || null };
  // فقط وقتی واقعاً از دفترچه آمده — کلیدِ `undefined` روی هر شیء، مقایسه‌ی
  // ساختاری (و تست‌ها) را بی‌دلیل به هم می‌ریزد.
  if (opts.contactId != null) fresh.contactId = opts.contactId;

  const key = keyOf(fresh);
  const taken = new Set<string>([...list.map(keyOf), ...(opts.taken ?? [])]);
  if (taken.has(key)) return { ok: false, reason: 'duplicate' };

  const room = opts.room?.[channel] ?? MAX_EMAIL_INVITES;
  if (list.filter((c) => c.channel === channel).length >= Math.max(0, room)) {
    return { ok: false, reason: 'full' };
  }

  return { ok: true, list: [...list, fresh] };
}

export const removeDraftContact = (
  list: readonly DraftContact[], channel: InviteChannel, value: string,
): DraftContact[] => list.filter((c) => !(c.channel === channel && c.value === value));

/** آنچه روی چیپ نوشته می‌شود: اسم اگر بود، وگرنه خودِ آدرس. */
export const draftContactLabel = (c: DraftContact): string => c.name || c.value;

/** خوراکِ `inviteRepo.byEmail`. */
export const emailInvitesOf = (
  list: readonly DraftContact[],
): { email: string; name: string | null }[] => list
  .filter((c) => c.channel === 'email')
  .map((c) => ({ email: c.value, name: c.name }));

/** خوراکِ `inviteRepo.bySms`. */
export const phoneInvitesOf = (
  list: readonly DraftContact[],
): { phone: string; name: string | null }[] => list
  .filter((c) => c.channel === 'sms')
  .map((c) => ({ phone: c.value, name: c.name }));

/** حداقلی که از یک ردیفِ `signal_recipients` برای این حساب‌وکتاب لازم است. */
export interface InvitedRowLike {
  invitedEmail?: string | null;
  invitedPhone?: string | null;
}

export interface InvitedSoFar {
  /** چند نفرِ دیگر از هر کانال جا هست. */
  room: Record<InviteChannel, number>;
  /** آدرس‌هایی که دوباره فرستادنشان بی‌اثر است. */
  taken: string[];
}

/**
 * وضعیتِ فعلیِ دعوت‌های بیرون از اپ روی یک سیگنال.
 *
 * ورودی همان ردیف‌های `signalRepo.recipients` است — که فرمِ ویرایش از قبل
 * دارد. پس برای فهمیدنِ سقف و تکراری‌ها هیچ رفت‌وبرگشتِ تازه‌ای لازم نیست.
 */
export function invitedSoFar(
  invited: readonly InvitedRowLike[],
  max = MAX_EMAIL_INVITES,
): InvitedSoFar {
  const emails = invited.map((r) => normalizeEmail(r.invitedEmail)).filter((e): e is string => !!e);
  // شماره‌ها توی دیتابیس از قبل بین‌المللی‌اند؛ کلید همان ده رقمِ آخر است،
  // همان چیزی که ایندکسِ یکتاییِ مایگریشن 0067 هم با آن کار می‌کند.
  const phones = invited.map((r) => phoneKey(r.invitedPhone)).filter((p): p is string => !!p);
  return {
    room: {
      email: Math.max(0, max - emails.length),
      sms: Math.max(0, max - phones.length),
    },
    taken: [...emails.map((e) => `email:${e}`), ...phones.map((p) => `sms:${p}`)],
  };
}

/* ــــ مخاطبِ دفترچه به‌عنوان یک ردیفِ آماده (WHOSUP-231) ــــ */

/**
 * حداقلی که از یک مخاطبِ دفترچه لازم است.
 *
 * موبایل این ردیف را `displayName` صدا می‌زند و وب `name`؛ هر کدام قبل از
 * صدا زدنِ {@link contactInvitees} به همین شکل درش می‌آورد، تا این‌جا یک
 * قاعده بماند نه دو تا.
 */
export interface BookContact {
  id: number;
  name: string;
  phones: readonly string[];
  emails: readonly string[];
  /** پر باشد یعنی عضو شده و ردیفِ دوستی نماینده‌ی اوست، نه این ردیف. */
  matchedUserId?: string | null;
}

/** یک مخاطبِ دفترچه، آماده‌ی نشستن در انتخابگرِ دعوت. */
export interface ContactInvitee<T> {
  contact: T;
  /** `null` یعنی دیده می‌شود ولی از این‌جا دعوت‌شدنی نیست. */
  channel: InviteChannel | null;
  /** آدرسی که به {@link addDraftContact} می‌رود؛ با `channel: null` هم null است. */
  value: string | null;
}

/**
 * مخاطبینِ دوست‌شده‌ی دفترچه که فرمِ ویرایش باید نشانشان بدهد (WHOSUP-231).
 *
 * ═══ حفره‌ای که این می‌بندد ═══
 * فرمِ **ساخت** دو منبع دارد — دوستانِ اپ و دوستانِ دفترچه — ولی فرمِ
 * **ویرایش** فقط منبعِ اول را داشت. یعنی کسی که در «سیگنال جدید» یک‌ضرب
 * انتخاب‌شدنی بود، در «ویرایش» اصلاً در فهرست نبود و کاربر باید ایمیلی را
 * که از قبل در دفترچه‌اش داشت از حفظ دوباره تایپ می‌کرد.
 *
 * سه قاعده این‌جا جمع شده‌اند:
 *   • **یک آدم، یک ردیف** — `contactFriendsToShow`: کسی که حساب
 *     دارد را ردیفِ دوستی نمایندگی می‌کند، نه ردیفِ دفترچه‌ای.
 *   • **از چه راهی می‌رسد** — موبایل پیامک را مقدم می‌داند (دستِ خودِ کاربر
 *     می‌رود)، مرورگر اصلاً پیامک ندارد پس `sms: false` می‌دهد و دوستِ
 *     فقط-شماره دیده می‌شود ولی انتخاب‌شدنی نیست (WHOSUP-143).
 *   • **قبلاً دعوت شده** — `taken`ِ {@link invitedSoFar}: ردیفی که دوباره
 *     فرستادنش بی‌اثر است اصلاً نشان داده نمی‌شود، وگرنه کاربر انتخابش
 *     می‌کرد، ذخیره سبز می‌شد و هیچ دعوتی نمی‌رفت.
 *
 * کسی که هیچ راهِ رسیدنی ندارد هم می‌ماند، فقط با `channel: null`: غیبِ
 * ناگهانیِ یک دوست از فهرست بدتر از یک ردیفِ خاکستری با دلیل است.
 */
export function contactInvitees<T extends BookContact>(args: {
  contacts: readonly T[];
  /** آدرس‌هایی که روی همین سیگنال دعوت شده‌اند، از {@link invitedSoFar}. */
  taken?: readonly string[];
  /** کدِ کشورِ پیش‌فرضِ شماره‌ی بدونِ «+» — از `phoneRepo.myCallingCode()`. */
  callingCode?: string;
  /** پیامک از این‌جا می‌رود؟ موبایل آری، مرورگر نه. */
  sms: boolean;
}): ContactInvitee<T>[] {
  const taken = new Set(args.taken ?? []);
  const rows: ContactInvitee<T>[] = [];

  for (const c of contactFriendsToShow(args.contacts)) {
    const email = normalizeEmail(c.emails[0]);
    const phone = normalizePhone(c.phones[0], args.callingCode ?? '1');

    // ترتیبِ `reachOf` (lib/roster.ts) وقتی پیامک هست؛ وگرنه فقط ایمیل.
    const pick: ContactInvitee<T> = args.sms && phone
      ? { contact: c, channel: 'sms', value: phone }
      : email
        ? { contact: c, channel: 'email', value: email }
        : { contact: c, channel: null, value: null };

    if (pick.value) {
      const key = pick.channel === 'email'
        ? `email:${pick.value}`
        : `sms:${phoneKey(pick.value) ?? pick.value}`;
      if (taken.has(key)) continue;
    }
    rows.push(pick);
  }

  return rows.sort((a, b) => a.contact.name.localeCompare(b.contact.name));
}
